import {
  ACADEMY_BASE_PATH,
  ACADEMY_CHAPTERS,
  ACADEMY_SEARCH_MIN_LENGTH,
  ACADEMY_SEARCH_PARAM,
  ACADEMY_SEARCH_SNIPPET_CONTEXT,
  ACADEMY_SECTION_PARAM,
  ACADEMY_SHORT_PATH,
  ACADEMY_SLIDE_PARAM,
  ACADEMY_SLIDES,
} from "./RentmatchAcademy.constants";
import { IAcademySearchResult } from "./RentmatchAcademy.types";
import {
  getAcademyText,
  getChapterKey,
  getSectionKey,
  getSlideKey,
  getUiKey,
} from "./RentmatchAcademy.utils";

const ELLIPSIS = "…";

export interface IAcademySearchEntry {
  id: string;
  title: string;
  // Where the entry sits: the chapter title, or the short version's title.
  context: string;
  // The texts searched, the title first: a match there ranks the entry higher.
  texts: string[];
  path: string;
  // Which section or slide to open; the query is added when it is a result.
  params: { [key: string]: string };
}

interface INormalizedText {
  value: string;
  // For every character of `value`, its index in the original text, so a match
  // found without accents can be cut out of the text with them.
  origins: number[];
}

// Lower case and without accents, character by character: "Café" becomes
// "cafe" while every character still points back at the one it came from.
export const normalizeAcademyText = (text: string): INormalizedText => {
  const origins: number[] = [];
  let value = "";
  let offset = 0;
  for (const character of text) {
    const plain = character.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    value += plain;
    for (let index = 0; index < plain.length; index += 1) {
      origins.push(offset);
    }
    offset += character.length;
  }

  return { value, origins };
};

// The words of a query, each searched for on its own: "rules score" finds
// "Your score-rules".
export const getSearchTerms = (query: string) =>
  normalizeAcademyText(query)
    .value.split(/[^\p{L}\p{N}]+/u)
    .filter(term => term.length > 0);

export const isSearchQuery = (query: string) =>
  normalizeAcademyText(query).value.trim().length >= ACADEMY_SEARCH_MIN_LENGTH;

// Body copy without its bullet markers and line breaks, as one line.
const toPlainText = (text: string) =>
  text
    .split("\n")
    .map(line => line.trim().replace(/^- /, ""))
    .filter(line => line.length > 0)
    .join(" ");

// Every occurrence of every term, as [start, end) in the original text,
// merged where they overlap.
export const findMatchRanges = (text: string, terms: string[]) => {
  const { value, origins } = normalizeAcademyText(text);
  const ranges: [number, number][] = [];
  terms.forEach(term => {
    let from = value.indexOf(term);
    while (from !== -1) {
      const last = from + term.length - 1;
      const end = origins[last + 1] ?? text.length;
      ranges.push([origins[from], end]);
      from = value.indexOf(term, from + term.length);
    }
  });

  return ranges
    .sort((a, b) => a[0] - b[0])
    .reduce<[number, number][]>((merged, range) => {
      const previous = merged[merged.length - 1];
      if (previous && range[0] <= previous[1]) {
        previous[1] = Math.max(previous[1], range[1]);
      } else {
        merged.push([...range]);
      }

      return merged;
    }, []);
};

// A piece of the text around the first match, cut at word boundaries.
export const getSnippet = (text: string, terms: string[]) => {
  const [first] = findMatchRanges(text, terms);
  if (!first) {
    return text.length > ACADEMY_SEARCH_SNIPPET_CONTEXT * 2
      ? `${text.slice(0, ACADEMY_SEARCH_SNIPPET_CONTEXT * 2).trim()}${ELLIPSIS}`
      : text;
  }

  let start = Math.max(0, first[0] - ACADEMY_SEARCH_SNIPPET_CONTEXT);
  let end = Math.min(text.length, first[1] + ACADEMY_SEARCH_SNIPPET_CONTEXT);
  if (start > 0) {
    const space = text.indexOf(" ", start);
    start = space !== -1 && space < first[0] ? space + 1 : start;
  }
  if (end < text.length) {
    const space = text.lastIndexOf(" ", end);
    end = space > first[1] ? space : end;
  }

  return `${start > 0 ? ELLIPSIS : ""}${text.slice(start, end).trim()}${
    end < text.length ? ELLIPSIS : ""
  }`;
};

const withParams = (path: string, params: { [key: string]: string }) =>
  `${path}?${new URLSearchParams(params).toString()}`;

// Everything a broker reads in the academy, as search entries: each chapter's
// title and intro, each section (or FAQ question with its answer) and each
// slide of the short version. The check questions are left out, so a search
// does not hand out their answers.
export const getAcademySearchEntries = (): IAcademySearchEntry[] => [
  ...ACADEMY_CHAPTERS.flatMap(chapter => {
    const chapterTitle = getAcademyText(getChapterKey(chapter.id, "title"));
    const chapterPath = `${ACADEMY_BASE_PATH}/${chapter.id}`;

    return [
      {
        id: chapter.id,
        title: chapterTitle,
        context: chapterTitle,
        texts: [
          chapterTitle,
          getAcademyText(getChapterKey(chapter.id, "intro")),
        ],
        path: chapterPath,
        params: {},
      },
      ...chapter.sections.map(section => {
        const title = getAcademyText(
          getSectionKey(chapter.id, section.id, "title"),
        );

        return {
          id: `${chapter.id}.${section.id}`,
          title,
          context: chapterTitle,
          texts: [
            title,
            toPlainText(
              getAcademyText(getSectionKey(chapter.id, section.id, "body")),
            ),
          ],
          path: chapterPath,
          params: { [ACADEMY_SECTION_PARAM]: section.id },
        };
      }),
    ];
  }),
  ...ACADEMY_SLIDES.map(slide => {
    const title = getAcademyText(getSlideKey(slide.id, "title"));

    return {
      id: `slide.${slide.id}`,
      title,
      context: getAcademyText(getUiKey("short.title")),
      texts: [
        title,
        getAcademyText(getSlideKey(slide.id, "label")),
        toPlainText(getAcademyText(getSlideKey(slide.id, "body"))),
      ],
      path: `${ACADEMY_BASE_PATH}/${ACADEMY_SHORT_PATH}`,
      params: { [ACADEMY_SLIDE_PARAM]: slide.id },
    };
  }),
];

// An entry matches when every word of the query appears in it, in any of its
// texts; entries whose title holds a word come first, in academy order.
export const searchAcademy = (
  query: string,
  entries = getAcademySearchEntries(),
): IAcademySearchResult[] => {
  if (!isSearchQuery(query)) {
    return [];
  }
  const terms = getSearchTerms(query);

  return entries
    .map((entry, index) => {
      const normalized = entry.texts.map(
        text => normalizeAcademyText(text).value,
      );
      const isMatch = terms.every(term =>
        normalized.some(text => text.includes(term)),
      );
      if (!isMatch) {
        return undefined;
      }
      const isTitleMatch = terms.some(term => normalized[0].includes(term));
      // The snippet comes from the first text past the title that matches, so
      // it adds to the title instead of repeating it.
      const snippetSource =
        entry.texts
          .slice(1)
          .find(text => findMatchRanges(text, terms).length > 0) ??
        entry.texts[entry.texts.length - 1];

      return {
        index,
        isTitleMatch,
        result: {
          id: entry.id,
          title: entry.title,
          context: entry.context,
          snippet: getSnippet(snippetSource, terms),
          path: withParams(entry.path, {
            ...entry.params,
            [ACADEMY_SEARCH_PARAM]: query.trim(),
          }),
        },
      };
    })
    .filter(<T>(item: T | undefined): item is T => item !== undefined)
    .sort(
      (a, b) =>
        Number(b.isTitleMatch) - Number(a.isTitleMatch) || a.index - b.index,
    )
    .map(({ result }) => result);
};
